import * as React from "react";
import {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useLazyQuery, useMutation} from "@apollo/client";
import {
    createOrganizationPlanMutation,
    deleteOrganizationPlanMutation,
    getOrganizationPlansQuery,
    updateOrganizationPlanMutation
} from "../../components/area/areaQueries";
import {UserContext, UserContextI} from "../../UserContext";
import {useConfirmDialog} from "../../components/common/ConfirmDialogProvider";
import {
    Button,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    Grid,
    HStack,
    IconButton,
    Input,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Stack,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    useToast
} from "@chakra-ui/react";
import {BiArrowBack, BiCog, BiEdit, BiPlus, BiTrashAlt} from "react-icons/bi";
import AuthButton from "../../auth/AuthButton";
import {useSearchParams} from "react-router-dom";
import {isRootAdminRole} from "../../auth/types";
import {OrganizationPlan} from "../../components/types";

type DialogMode = "create" | "edit";

const isWholeNumber = (value: string) => /^\d+$/.test(value.trim());

const OrganizationPlanManagement = () => {
    const userContext: UserContextI = useContext<UserContextI>(UserContext);
    const {confirm} = useConfirmDialog();
    const toast = useToast();
    const [search, setSearch] = useSearchParams();

    const [getOrganizationPlans, {data: organizationPlansData, loading: loadingPlans}] = useLazyQuery(getOrganizationPlansQuery);
    const [createOrganizationPlan, {loading: creatingPlan}] = useMutation(createOrganizationPlanMutation);
    const [updateOrganizationPlan, {loading: updatingPlan}] = useMutation(updateOrganizationPlanMutation);
    const [deleteOrganizationPlan, {loading: deletingPlan}] = useMutation(deleteOrganizationPlanMutation);

    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [dialogMode, setDialogMode] = useState<DialogMode>("create");
    const [editingPlanId, setEditingPlanId] = useState<string>("");
    const [planName, setPlanName] = useState<string>("");
    const [maxAdministrators, setMaxAdministrators] = useState<string>("1");
    const [maxEditors, setMaxEditors] = useState<string>("2");

    const user = userContext.getUser();
    const isRootAdmin = isRootAdminRole(user?.role);

    const organizationPlans = useMemo<OrganizationPlan[]>(() => {
        const list = organizationPlansData?.getOrganizationPlans || [];
        return list
            .map((plan: any) => ({
                id: plan?.id,
                name: plan?.name || "",
                maxAdministrators: plan?.maxAdministrators ?? 0,
                maxEditors: plan?.maxEditors ?? 0,
                isDefault: plan?.isDefault === true,
                assignedOrganizationsCount: plan?.assignedOrganizationsCount ?? 0
            }))
            .filter((plan: OrganizationPlan) => !!plan.id)
            .sort((a: OrganizationPlan, b: OrganizationPlan) => a.name.localeCompare(b.name, undefined, {sensitivity: "base"}));
    }, [organizationPlansData]);

    const loadPlans = useCallback(() => {
        if (!isRootAdmin) {
            return;
        }
        getOrganizationPlans({
            fetchPolicy: "network-only"
        });
    }, [getOrganizationPlans, isRootAdmin]);

    useEffect(() => {
        loadPlans();
    }, [loadPlans]);

    const backToOrganizations = () => {
        search.set("view", "organizations");
        setSearch(search);
    };

    const openOrganizationManagement = () => {
        search.set("view", "organizations");
        setSearch(search);
    };

    const openUserManagement = () => {
        search.set("view", "users");
        setSearch(search);
    };

    const openPlanManagement = () => {
        search.set("view", "plans");
        setSearch(search);
    };

    const closeDialog = () => {
        if (creatingPlan || updatingPlan) {
            return;
        }
        setDialogOpen(false);
        setDialogMode("create");
        setEditingPlanId("");
        setPlanName("");
        setMaxAdministrators("1");
        setMaxEditors("2");
    };

    const openCreateDialog = () => {
        setDialogMode("create");
        setEditingPlanId("");
        setPlanName("");
        setMaxAdministrators("1");
        setMaxEditors("2");
        setDialogOpen(true);
    };

    const openEditDialog = (plan: OrganizationPlan) => {
        setDialogMode("edit");
        setEditingPlanId(plan.id);
        setPlanName(plan.name);
        setMaxAdministrators(String(plan.maxAdministrators));
        setMaxEditors(String(plan.maxEditors));
        setDialogOpen(true);
    };

    const handleSavePlan = async () => {
        const trimmedName = planName.trim();
        if (!trimmedName || !isWholeNumber(maxAdministrators) || !isWholeNumber(maxEditors)) {
            return;
        }
        try {
            if (dialogMode === "create") {
                await createOrganizationPlan({
                    variables: {
                        name: trimmedName,
                        maxAdministrators: Number.parseInt(maxAdministrators, 10),
                        maxEditors: Number.parseInt(maxEditors, 10)
                    }
                });
            } else {
                await updateOrganizationPlan({
                    variables: {
                        id: editingPlanId,
                        name: trimmedName,
                        maxAdministrators: Number.parseInt(maxAdministrators, 10),
                        maxEditors: Number.parseInt(maxEditors, 10)
                    }
                });
            }
            closeDialog();
            loadPlans();
        } catch (error) {
            console.error(error);
            const message = error instanceof Error ? error.message : "Unable to save organization plan.";
            toast({
                title: "Organization plan save failed",
                description: message,
                status: "error",
                isClosable: true
            });
        }
    };

    const handleDeletePlan = async (plan: OrganizationPlan) => {
        const approved = await confirm({
            title: "Delete organization plan",
            body: `Delete "${plan.name}"?`,
            confirmText: "Delete",
            isDestructive: true
        });
        if (!approved) {
            return;
        }
        try {
            await deleteOrganizationPlan({
                variables: {
                    id: plan.id
                }
            });
            loadPlans();
        } catch (error) {
            console.error(error);
            const message = error instanceof Error ? error.message : "Unable to delete organization plan.";
            toast({
                title: "Organization plan delete failed",
                description: message,
                status: "error",
                isClosable: true
            });
        }
    };

    if (!isRootAdmin) {
        return <Grid width={"inherit"} id={"Grid"} gridTemplateRows={"40px 1fr 40px"}>
            <Grid gridTemplateColumns={"1fr max-content"} alignItems={"center"} columnGap={2}>
                <Button size={"sm"} leftIcon={<BiArrowBack/>} onClick={backToOrganizations}>
                    Back to organizations
                </Button>
                <AuthButton/>
            </Grid>
            <div>Unauthorized</div>
            <div/>
        </Grid>;
    }

    const isPlanNameInvalid = planName.trim().length === 0;
    const isMaxAdministratorsInvalid = !isWholeNumber(maxAdministrators);
    const isMaxEditorsInvalid = !isWholeNumber(maxEditors);

    return <Grid width={"inherit"} id={"Grid"} gridTemplateRows={"40px minmax(0, 1fr) 40px"}>
        <Grid gridTemplateColumns={"max-content 1fr max-content max-content"} alignItems={"center"} columnGap={2}>
            <Button size={"sm"} leftIcon={<BiArrowBack/>} onClick={backToOrganizations}>
                Back to organizations
            </Button>
            <div/>
            <Menu>
                <MenuButton
                    as={IconButton}
                    aria-label={"Management actions"}
                    icon={<BiCog/>}
                    size={"sm"}
                    variant={"outline"}
                />
                <MenuList zIndex={20}>
                    <MenuItem onClick={openOrganizationManagement}>
                        Manage organizations
                    </MenuItem>
                    <MenuItem onClick={openUserManagement}>
                        Manage users
                    </MenuItem>
                    <MenuItem onClick={openPlanManagement}>
                        Manage organization plans
                    </MenuItem>
                </MenuList>
            </Menu>
            <AuthButton/>
        </Grid>
        <Grid minH={0} overflowY={"auto"}>
            <Stack spacing={3}>
                <HStack justifyContent={"space-between"} alignItems={"center"}>
                    <Text fontSize={"lg"} fontWeight={"semibold"}>Organization plans</Text>
                    <IconButton
                        aria-label={"Add plan"}
                        icon={<BiPlus/>}
                        size={"sm"}
                        variant={"ghost"}
                        onClick={openCreateDialog}
                    />
                </HStack>
                <Table size={"sm"} variant={"simple"}>
                    <Thead>
                        <Tr>
                            <Th>Plan</Th>
                            <Th>Administrator limit</Th>
                            <Th>Editor limit</Th>
                            <Th>Organizations using plan</Th>
                            <Th width={"1%"} whiteSpace={"nowrap"}>Actions</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {organizationPlans.map((plan: OrganizationPlan) => {
                            const deleteDisabledReason = plan.isDefault ?
                                "The default plan cannot be deleted." :
                                (plan.assignedOrganizationsCount > 0 ?
                                    `Used by ${plan.assignedOrganizationsCount} organization(s). Reassign them before deleting.` :
                                    "");
                            const isDeleteDisabled = !!deleteDisabledReason;
                            return (
                                <Tr key={plan.id}>
                                    <Td>
                                        {plan.name}
                                        {plan.isDefault ? " (Default)" : ""}
                                    </Td>
                                    <Td>{plan.maxAdministrators}</Td>
                                    <Td>{plan.maxEditors}</Td>
                                    <Td>{plan.assignedOrganizationsCount}</Td>
                                    <Td>
                                        <HStack spacing={1}>
                                            <Tooltip label={"Edit plan"}>
                                                <IconButton
                                                    aria-label={"Edit plan"}
                                                    icon={<BiEdit/>}
                                                    size={"sm"}
                                                    variant={"ghost"}
                                                    onClick={() => openEditDialog(plan)}
                                                />
                                            </Tooltip>
                                            <Tooltip label={deleteDisabledReason || "Delete plan"} shouldWrapChildren>
                                                <IconButton
                                                    aria-label={"Delete plan"}
                                                    icon={<BiTrashAlt/>}
                                                    size={"sm"}
                                                    variant={"ghost"}
                                                    colorScheme={"red"}
                                                    isDisabled={isDeleteDisabled || deletingPlan}
                                                    onClick={() => handleDeletePlan(plan)}
                                                />
                                            </Tooltip>
                                        </HStack>
                                    </Td>
                                </Tr>
                            );
                        })}
                        {organizationPlans.length === 0 && !loadingPlans ? (
                            <Tr>
                                <Td colSpan={5}>No plans</Td>
                            </Tr>
                        ) : null}
                    </Tbody>
                </Table>
            </Stack>
        </Grid>
        <div/>

        <Modal isOpen={dialogOpen} onClose={closeDialog} isCentered>
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>{dialogMode === "create" ? "Add organization plan" : "Edit organization plan"}</ModalHeader>
                <ModalCloseButton isDisabled={creatingPlan || updatingPlan}/>
                <ModalBody>
                    <Stack spacing={4}>
                        <FormControl isInvalid={isPlanNameInvalid}>
                            <FormLabel>Name</FormLabel>
                            <Input
                                value={planName}
                                onChange={(event) => setPlanName(event.target.value)}
                                placeholder={"Plan name"}
                                autoFocus
                            />
                            {isPlanNameInvalid ? (
                                <FormErrorMessage>Name is required.</FormErrorMessage>
                            ) : null}
                        </FormControl>
                        <FormControl isInvalid={isMaxAdministratorsInvalid}>
                            <FormLabel>Administrator limit</FormLabel>
                            <Input
                                value={maxAdministrators}
                                onChange={(event) => setMaxAdministrators(event.target.value)}
                                inputMode={"numeric"}
                            />
                            <FormHelperText>
                                Maximum number of administrators allowed per organization on this plan.
                            </FormHelperText>
                            {isMaxAdministratorsInvalid ? (
                                <FormErrorMessage>Enter a whole number greater than or equal to 0.</FormErrorMessage>
                            ) : null}
                        </FormControl>
                        <FormControl isInvalid={isMaxEditorsInvalid}>
                            <FormLabel>Editor limit</FormLabel>
                            <Input
                                value={maxEditors}
                                onChange={(event) => setMaxEditors(event.target.value)}
                                inputMode={"numeric"}
                            />
                            <FormHelperText>
                                Maximum number of editors allowed per organization on this plan.
                            </FormHelperText>
                            {isMaxEditorsInvalid ? (
                                <FormErrorMessage>Enter a whole number greater than or equal to 0.</FormErrorMessage>
                            ) : null}
                        </FormControl>
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Button variant={"ghost"} mr={3} onClick={closeDialog} isDisabled={creatingPlan || updatingPlan}>
                        Cancel
                    </Button>
                    <Button
                        colorScheme={"blue"}
                        onClick={handleSavePlan}
                        isLoading={creatingPlan || updatingPlan}
                        isDisabled={isPlanNameInvalid || isMaxAdministratorsInvalid || isMaxEditorsInvalid}
                    >
                        Save
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    </Grid>;
};

export default OrganizationPlanManagement;
